// ... existing imports remain the same
import React, { useEffect, useState } from "react";
import { FaArrowLeft, FaBook, FaGraduationCap, FaClock, FaQuestionCircle, FaUser, FaCheckCircle, FaListUl, FaPlay, FaRandom, FaPencilAlt } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { API_BASE_URL } from "../../config/apiConfig";
import { useQuiz } from "../../utils/api/Redux/QuizContext";
import { ClipLoader } from "react-spinners";
import toast, { Toaster } from "react-hot-toast";


const parseSubjects = (subjectsString) => {
    try {
        return JSON.parse(subjectsString || "[]");
    } catch {
        return [];
    }
};

const TakeSessionFree = () => {
    const navigate = useNavigate();
    const { mode } = useParams(); // exam, study, or random
    const { setQuizData } = useQuiz();

    const [userData, setUserData] = useState(null);
    const [quizData, setQuizBank] = useState([]);
    const [selectedSubject, setSelectedSubject] = useState(null);
    const [availableTopics, setAvailableTopics] = useState([]);
    const [selectedTopics, setSelectedTopics] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const isFreeUser = true

    // Fetch user profile
    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/free-users/profile`, {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                });
                if (!response.ok) throw new Error("Failed to fetch user profile");

                const user = await response.json();
                setUserData(user);
            } catch (error) {
                console.error("Error fetching profile:", error.message);
            }
        };

        fetchProfile();
    }, []);

    // Fetch all questions
    useEffect(() => {
        const fetchQuestions = async () => {
            setIsLoading(true);

            try {
                const response = await fetch(`${API_BASE_URL}/api/quiz/subjects-free`, {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                });

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.message || "Failed to fetch quiz questions");
                }

                if (!userData) return;

                // FIX: parse subjects properly AFTER userData loads
                const subjects = Array.isArray(userData.subjects)
                    ? userData.subjects
                    : JSON.parse(userData.subjects || "[]");

                // FIX: use correct data structure
                const filtered = (data.questions || []).filter(
                    (q) =>
                        q.class === userData.yearGroup &&
                        subjects.includes(q.subject)
                );

                setQuizBank(filtered);

            } catch (error) {
                console.error(error);
            } finally {
                setIsLoading(false);
            }
        };

        if (userData) fetchQuestions();
    }, [userData]);




    // Subjects allowed for this user
    const userSubjects = userData?.subjects || [];

    // Watch selectedSubject → update availableTopics
    useEffect(() => {
        if (mode === "study" && selectedSubject) {
            const subjectQuestions = quizData.filter(
                (q) =>
                    q.class === userData?.yearGroup &&
                    q.subject === selectedSubject
            );

            // Extract unique topics
            const topics = [
                ...new Set(subjectQuestions.map((q) => q.topic).filter(Boolean)),
            ];

            setAvailableTopics(topics);
            setSelectedTopics([]); // reset whenever subject changes
        }
    }, [selectedSubject, mode, quizData, userData]);

    // Filter questions
    const getFilteredQuestions = () => {
        let questions = quizData.filter(
            (q) => q.class === userData?.yearGroup
        );

        if (selectedSubject) {
            questions = questions.filter((q) => q.subject === selectedSubject);
        }

        // If study mode and topics chosen → filter by those
        if (mode === "study" && selectedTopics.length > 0) {
            questions = questions.filter((q) =>
                selectedTopics.includes(q.topic)
            );
        }

        return questions;
    };

    // Create new test
    const createTest = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/tests-free`, {
                method: "GET", // consider POST if creating new
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to create test");
            }

            const test = await response.json();
            console.log(test)
            localStorage.setItem("test_id", test.data.id);
            return test.data.id;
        } catch (error) {
            console.error("Error creating test:", error.message);
            return null;
        }
    };

    // Start Quiz/Exam
    const startQuiz = async (quizMode = mode) => {
        if (!quizMode) return;

        let selectedQuestions = getFilteredQuestions();

        if (selectedQuestions.length === 0) {
            toast.error("No questions available for this subject/topic.");
            return;
        }

        // Apply mode-specific logic
        if (quizMode === "exam") {
            selectedQuestions = selectedQuestions
                .sort(() => Math.random() - 0.5)
                .slice(0, 40);
        } else if (quizMode === "study") {
            if (selectedTopics.length === 0) {
                // Random study if no topics chosen
                selectedQuestions = selectedQuestions
                    .sort(() => Math.random() - 0.5)
                    .slice(0, 20);
            }
        } else if (quizMode === "random") {
            selectedQuestions = selectedQuestions
                .sort(() => Math.random() - 0.5)
                .slice(0, 10);
        }

        setQuizData(selectedQuestions);

        // Create test in backend
        const testId = await createTest();
        console.log(testId)
        if (!testId) {
            alert("Could not create test. Please try again.");
            return;
        }

        const timeConfig = {
            exam: 6300,   // 1 hr 45 min
            study: 3600,
            random: 1800,
        };


        navigate("/exam-free", {
            state: {
                test_id: testId,
                time_left: timeConfig[quizMode] || 3600,
                mode: quizMode,
                subject: selectedSubject,
                topics: selectedTopics,
                yearGroup: userData?.yearGroup,
            },
        });
    };

    // Get mode icon and info
    const getModeInfo = () => {
        switch (mode) {
            case "exam":
                return {
                    icon: <FaGraduationCap className="text-2xl" />,
                    title: "Take Exam",
                    description: "10 questions • • 1 hour 45 minutes",
                    color: "text-blue-600",
                    bgColor: "bg-blue-50",
                    borderColor: "border-blue-200"
                };
            case "study":
                return {
                    icon: <FaBook className="text-2xl" />,
                    title: "Study Mode",
                    description: "10 questions • 1 hour",
                    color: "text-blue-600",
                    bgColor: "bg-blue-50",
                    borderColor: "border-blue-200"
                };
            case "random":
                return {
                    icon: <FaRandom className="text-2xl" />,
                    title: "Random Mode",
                    description: "10 questions • 30 minutes",
                    color: "text-purple-600",
                    bgColor: "bg-purple-50",
                    borderColor: "border-purple-200"
                };
            default:
                return {
                    icon: <FaQuestionCircle className="text-2xl" />,
                    title: "Quiz Mode",
                    description: "",
                    color: "text-blue-600",
                    bgColor: "bg-blue-50",
                    borderColor: "border-blue-200"
                };
        }
    };

    const modeInfo = getModeInfo();
    const totalQuestions = getFilteredQuestions().length;
    const subjectQuestions = selectedSubject ? quizData.filter(q => q.subject === selectedSubject).length : 0;

    if (isLoading || !userData) {
        return (
            <div className="flex items-center justify-center h-screen bg-gray-100">
                <div className="text-center">
                    <ClipLoader color="#2148C0" size={50} />
                    <p className="ml-3 text-[#2148C0] font-medium mt-4">Loading your exam page...</p>
                </div>
            </div>
        );
    }

    return (
        <div>
            {/* Back Button */}
            <div className="flex items-center mb-2 mt-6">
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center ml-[30px] mt-8 gap-2 text-[#2148C0] hover:text-blue-600 font-medium transition-all duration-200 hover:gap-3"
                >
                    <FaArrowLeft className="text-xl" />
                    Back
                </button>
            </div>
            <Toaster position="top-right" reverseOrder={false} />

            {/* Main Content */}
            <div className="min-h-screen bg-gray-50 py-10 px-6">
                <div className="max-w-4xl mx-auto bg-white p-8 rounded-xl shadow-lg">
                    {/* Header with Mode Info */}
                    <div className={`${modeInfo.bgColor} ${modeInfo.borderColor} border-2 rounded-xl p-6 mb-8`}>
                        <div className="flex items-center justify-center gap-4 mb-4">
                            <div className={modeInfo.color}>
                                {modeInfo.icon}
                            </div>
                            <div className="text-center">
                                <h1 className={`text-3xl font-bold ${modeInfo.color} mb-2`}>
                                    {modeInfo.title}
                                </h1>
                                <p className="text-gray-600 flex items-center justify-center gap-2">
                                    <FaClock className="text-sm" />
                                    {modeInfo.description}
                                </p>
                            </div>
                        </div>

                        {/* User Info */}
                        <div className="flex flex-col sm:flex-row sm:justify-center sm:items-center gap-4 sm:gap-6 text-sm text-gray-600 border-t pt-4">
                            <div className="flex items-center gap-2">
                                <FaUser className="text-blue-500" />
                                <span>{userData?.name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <FaGraduationCap className="text-green-500" />
                                <span>{userData?.yearGroup}</span>
                            </div>
                            {/* <div className="flex items-center gap-2">
                                <FaQuestionCircle className="text-purple-500" />
                                <span>{totalQuestions} Total Questions</span>
                            </div> */}
                        </div>

                    </div>

                    {/* Free User Notice */}
                    {isFreeUser && (
                        <div className="bg-yellow-100 border-l-4 border-yellow-400 text-yellow-700 px-4 py-2 mb-4 text-sm rounded">
                            Note: Free accounts can only attempt up to <span className="font-semibold">10 questions per subject</span>.
                            Upgrade to a subscription for full access.
                        </div>
                    )}


                    {/* Subject Selection */}
                    <div className="mb-6">
                        <div className="flex items-center gap-3 mb-4">
                            <FaBook className="text-2xl text-[#2148C0]" />
                            <div>
                                <h2 className="text-xl font-semibold text-gray-700">
                                    Select Subject
                                </h2>
                                <p className="text-sm text-gray-500">
                                    {userSubjects.length} subjects available for {userData?.yearGroup}
                                </p>
                            </div>
                        </div>

                        {userSubjects.length === 0 ? (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                                <FaQuestionCircle className="text-red-500 text-2xl mx-auto mb-2" />
                                <p className="text-red-600 font-medium">
                                    No subjects assigned to your account
                                </p>
                                <p className="text-red-500 text-sm">
                                    Contact your admin/teacher for assistance
                                </p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {userSubjects.map((subject, index) => {
                                    const subjectQCount = quizData.filter(q => q.subject === subject).length;
                                    return (
                                        <label
                                            key={index}
                                            className={`relative cursor-pointer p-4 border-2 rounded-lg transition-all duration-200 hover:shadow-md ${selectedSubject === subject
                                                ? "border-blue-500 bg-blue-50 shadow-md"
                                                : "border-gray-200 hover:border-blue-300"
                                                }`}
                                        >
                                            <div className="flex items-start gap-3">
                                                <input
                                                    type="radio"
                                                    name="subject"
                                                    value={subject}
                                                    checked={selectedSubject === subject}
                                                    onChange={() => setSelectedSubject(subject)}
                                                    className="form-radio h-5 w-5 text-blue-600 mt-1"
                                                />
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <FaPencilAlt className="text-gray-400 text-sm" />
                                                        <span className="font-medium text-gray-800">
                                                            {subject}
                                                        </span>
                                                    </div>
                                                    {/* <div className="flex items-center gap-1 text-xs text-gray-500">
                                                        <FaQuestionCircle />
                                                        <span>{subjectQCount} questions</span>
                                                    </div> */}
                                                </div>
                                            </div>
                                            {selectedSubject === subject && (
                                                <FaCheckCircle className="absolute top-2 right-2 text-blue-500" />
                                            )}
                                        </label>
                                    );
                                })}
                            </div>
                        )}

                        {selectedSubject && (
                            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <div className="flex items-center gap-2 text-blue-700">
                                    <FaCheckCircle />
                                    <span className="font-medium">Selected: {selectedSubject}</span>
                                    {/* <span className="text-sm">({subjectQuestions} questions available)</span> */}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Topic Selection (Study mode only) */}
                    {/* {mode === "study" && selectedSubject && (
                        <div className="mb-6">
                            <div className="flex items-center gap-3 mb-4">
                                <FaListUl className="text-2xl text-green-600" />
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-700">
                                        Choose Topics
                                    </h2>
                                    <p className="text-sm text-gray-500">
                                        Leave empty for random questions from {selectedSubject}
                                    </p>
                                </div>
                            </div>

                            {availableTopics.length === 0 ? (
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                                    <FaListUl className="text-gray-400 text-2xl mx-auto mb-2" />
                                    <p className="text-gray-500">
                                        No topics found for this subject
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                        {availableTopics.map((topic, index) => {
                                            const topicQCount = quizData.filter(q => q.topic === topic && q.subject === selectedSubject).length;
                                            return (
                                                <label
                                                    key={index}
                                                    className={`cursor-pointer p-3 border rounded-lg transition-all duration-200 hover:shadow-sm ${selectedTopics.includes(topic)
                                                        ? "border-green-500 bg-green-50"
                                                        : "border-gray-200 hover:border-green-300"
                                                        }`}
                                                >
                                                    <div className="flex items-start gap-3">
                                                        <input
                                                            type="checkbox"
                                                            value={topic}
                                                            checked={selectedTopics.includes(topic)}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setSelectedTopics([...selectedTopics, topic]);
                                                                } else {
                                                                    setSelectedTopics(
                                                                        selectedTopics.filter((t) => t !== topic)
                                                                    );
                                                                }
                                                            }}
                                                            className="form-checkbox h-4 w-4 text-green-600 mt-1"
                                                        />
                                                        <div className="flex-1">
                                                            <span className="text-gray-800 font-medium text-sm block">
                                                                {topic}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </label>
                                            );
                                        })}
                                    </div>

                                    {selectedTopics.length > 0 && (
                                        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                                            <div className="flex items-center gap-2 text-green-700">
                                                <FaCheckCircle />
                                                <span className="font-medium">
                                                    {selectedTopics.length} topic{selectedTopics.length > 1 ? 's' : ''} selected
                                                </span>
                                            </div>
                                            <div className="flex flex-wrap gap-2 mt-2">
                                                {selectedTopics.map((topic, idx) => (
                                                    <span key={idx} className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                                                        {topic}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )} */}

                    {/* Start Button */}
                    <div className="text-center">
                        <button
                            onClick={() => startQuiz(mode)}
                            disabled={!selectedSubject}
                            className={`px-8 py-4 rounded-xl font-semibold text-lg shadow-lg transition-all duration-200 flex items-center gap-3 mx-auto ${selectedSubject
                                ? `${modeInfo.color.replace('text-', 'bg-')} hover:shadow-xl text-white transform hover:scale-105`
                                : "bg-gray-400 text-gray-200 cursor-not-allowed"
                                }`}
                        >
                            <FaPlay className="text-lg" />
                            Start {mode === "exam" ? "Exam" : mode === "study" ? "Study Session" : "Random Quiz"}
                        </button>

                        {selectedSubject && (
                            <p className="text-sm text-gray-500 mt-3">
                                Ready to begin with {getFilteredQuestions().length} questions
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="text-center text-gray-300 py-4 bg-[#2148C0] text-sm">
                <p className="flex items-center justify-center gap-2">
                    <FaGraduationCap />
                    &copy; {new Date().getFullYear()} TeeChaa CBT Application. All rights reserved.
                </p>
            </footer>
        </div>
    );
};

export default TakeSessionFree;